import { Matrix4 } from '@math.gl/core'
import { ForwardedRef, forwardRef, useContext, useMemo } from 'react'
import { Position } from 'types'
import { MediaSprite } from '../../../types'
import { Renderable } from '../classes/renderer'
import Shader from '../classes/shader'
import { ArtboardContext } from '../context'
import { useTexture } from '../hooks/use-texture'
import { ArtboardSpriteLayerProps } from '../types'
import { resizeSprite } from '../utilities/resize-sprite'
import { getSpriteTimelineProgress } from '../utilities/sprite-progress'
import ArtboardLayer from './base'

const ArtboardSprite = (
    { mirrored = false, reversed = false, source, ...rest }: ArtboardSpriteLayerProps,
    ref: ForwardedRef<Renderable>
) => {
    const { fps, frames, renderer } = useContext(ArtboardContext)

    const sprite: MediaSprite | null = useMemo(() => {
        if (!renderer || !source) {
            return null
        }

        return resizeSprite(source, renderer.gl.MAX_TEXTURE_SIZE)
    }, [renderer, source])

    const texture = useTexture(renderer, sprite?.canvas)

    function onDraw(position: Position, shader: Shader) {
        if (!source || !texture) return

        const { columns, duration, frameIndexes, rows } = source
        const progress = getSpriteTimelineProgress(
            shader.progress,
            {
                artboardFrameCount: frames,
                fps,
                spriteDuration: duration,
                spriteFrameCount: frameIndexes.length,
            },
            reversed
        )

        const frame = frameIndexes[Math.min(Math.floor(progress * frameIndexes.length), frameIndexes.length - 1)]
        const frameColumn = frame % columns
        const frameRow = Math.floor(frame / columns)
        const clipMatrix = new Matrix4()

        clipMatrix.translate([frameColumn / columns, frameRow / rows, 0])
        clipMatrix.scale([1 / columns, 1 / rows, 1])

        shader.drawImage({ ...position, mirrored, texture, clipMatrix })
    }

    return <ArtboardLayer {...rest} ref={ref} onDraw={onDraw} />
}

export default forwardRef(ArtboardSprite)
